import { Injectable } from '@angular/core';
import { map } from 'rxjs';

import { BusinessPurpose, Ticket } from 'src/app/models/ticket/ticket.model';
import { LoadTickets, LoadTicketsFail, LoadTicketsSuccess } from '../../store';
import { GaugeValueKPI } from '../types';
import { ChartType } from '../types/chart-type.types';
import { ChartService } from './chart.service';

enum RTAStates {
  GREEN = 0,
  YELLOW = 10,
  RED = 18,
}

const DEFAULT_MAX_VALUE = 24;
const DEFAULT_MIN_VALUE = RTAStates.GREEN;
const THRESHOLDS: GaugeValueKPI['thresholds'] = Object.freeze({
  [RTAStates.GREEN]: { color: '#3dcc64', bgOpacity: 0.2 },
  [RTAStates.YELLOW]: { color: '#ffbb2c', bgOpacity: 0.2 },
  [RTAStates.RED]: { color: '#D11314', bgOpacity: 0.2 },
});
const ALLOWED_BUSINESS_PURPOSES = Object.freeze([
  BusinessPurpose.MscBandwidthThroughput,
  BusinessPurpose.MscConfigurationChange,
  BusinessPurpose.MscCpeHardwareIssue,
  BusinessPurpose.MscCustomerFacilitiesIssue,
  BusinessPurpose.MscCustomerRfoRequest,
  BusinessPurpose.MscFirewallIssue,
  BusinessPurpose.MscLatencyPacketLoss,
  BusinessPurpose.MscNetworkOutage,
  BusinessPurpose.MscSdwanIssue,
  BusinessPurpose.MscServiceReview,
  BusinessPurpose.MscTechnicalQuestion,
  BusinessPurpose.MscUtilizationRequest,
  BusinessPurpose.MscVoipIssue,
  BusinessPurpose.NocBandwidthThroughput,
  BusinessPurpose.NocChronic,
  BusinessPurpose.NocCircuitDown,
  BusinessPurpose.NocCircuitDownBroadband,
  BusinessPurpose.NocCircuitDownDisconnected,
  BusinessPurpose.NocCircuitErrors,
  BusinessPurpose.NocCircuitUpNoSurf,
  BusinessPurpose.NocCosApplications,
  BusinessPurpose.NocCpeHardwareIssue,
  BusinessPurpose.NocCustomerFacilitiesIssue,
  BusinessPurpose.NocFirewallIssue,
  BusinessPurpose.NocLatencyPacketLoss,
  BusinessPurpose.NocNetworkOutage,
  BusinessPurpose.NocRoutingIssue,
  BusinessPurpose.NocSdwanIssue,
  BusinessPurpose.NocTechQuestion,
  BusinessPurpose.NocVoipCosMpls,
  BusinessPurpose.SdwanAddIps,
  BusinessPurpose.SdwanConfigChange,
  BusinessPurpose.SdwanGeneral,
  BusinessPurpose.SupportConfigChange,
  BusinessPurpose.SupportConfigRequest,
  BusinessPurpose.SupportDnsChange,
  BusinessPurpose.SupportIpRequest,
  BusinessPurpose.SupportSdwanConfigChange,
  BusinessPurpose.SupportUtilizationRequest,
]);

@Injectable()
export class MTTRAllNOCCasesLifeChartService extends ChartService<GaugeValueKPI> {
  public readonly chart$ = this.chartsDataLoader({
    request: LoadTickets,
    failure: LoadTicketsFail,
    success: LoadTicketsSuccess,
  }).pipe(
    map(({ loading, data }) => {
      let times = 0;
      let rta = 0;

      (Array.isArray(data?.data) ? data?.data : []).reduce(
        (acc, ticket) => {
          if (this.filterTickets(ticket)) {
            acc.times += 1;
            acc.rta += ticket.Time_to_Repair_Days__c || 0;
          }
          return acc;
        },
        { times: 0, rta: 0 },
      );

      // Assign the reduced values to times and rta
      const reduced = (Array.isArray(data?.data) ? data.data : []).reduce(
        (acc, ticket) => {
          if (this.filterTickets(ticket)) {
            acc.times += 1;
            acc.rta += ticket.Time_to_Repair_Days__c || 0;
          }
          return acc;
        },
        { times: 0, rta: 0 },
      );

      times = reduced.times;
      rta = reduced.rta;

      const dataValue = Number((rta / (times || 1)).toFixed(2));

      return {
        loading,
        ngClass: ['gauge-widget-label--limited'],
        data: {
          type: ChartType.GAUGE,
          id: 'MTTR All NOC Cases LIFE',
          title: 'Average Time to Resolve NOC Tickets',
          numberLabel: 'Restoration Time Average',
          records: loading ? undefined : times,
          value: dataValue,
          hyperlink: '/tickets',
          minValue: DEFAULT_MIN_VALUE,
          maxValue: Math.max(dataValue, DEFAULT_MAX_VALUE),
          markers: this.makeMarkers(dataValue),
          thresholds: { ...THRESHOLDS },
        } satisfies GaugeValueKPI,
      };
    }),
  );

  private filterTickets(ticket: Ticket): boolean {
    if (!ticket.IsClosed) return false;
    if (ticket.ClosedDate == null) return false;

    return ALLOWED_BUSINESS_PURPOSES.includes(
      ticket.Business_Purpose__c as BusinessPurpose,
    );
  }

  private makeMarkers(value: number): GaugeValueKPI['markers'] {
    const defaults = Object.freeze({
      color: '#888',
      type: 'line',
      size: 8,
    });

    return {
      [RTAStates.GREEN]: { ...defaults, label: `${RTAStates.GREEN}` },
      [RTAStates.YELLOW]: { ...defaults, label: `${RTAStates.YELLOW}` },
      [RTAStates.RED]: { ...defaults, label: `${RTAStates.RED}` },
      [DEFAULT_MAX_VALUE]: { ...defaults, label: `${DEFAULT_MAX_VALUE}` },
      [value]: {
        ...defaults,
        label: DEFAULT_MAX_VALUE < value ? `${value}` : '',
        type: 'triangle',
        color: '#000',
      },
    };
  }
}
